import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Generic, endpoint-agnostic asynchronous-search lifecycle hook.
 *
 * It owns the entire correctness-sensitive lifecycle — query normalization,
 * min/max length gating, debounce, abort-on-every-change, stale-response
 * guards, immediate result clearing, loading/results/empty/error state, reset
 * and unmount cleanup — and receives an *injected* endpoint-specific search
 * function. It imports no service and knows nothing about the URL, request
 * body, pageSize, or sort: those live in the injected `searchFn`.
 *
 * PR 1 injects a `/search/item` adapter. PR 2 (ITAM-2583) injects a
 * `/search/item_in_marketplace` adapter and reuses this hook unchanged.
 */

/**
 * Endpoint-specific search. Receives the NORMALIZED (trimmed) query and an
 * AbortSignal, returns the typed result rows. The hook never constructs the
 * request body or the URL.
 */
export type SearchFn<T> = (
  normalizedQuery: string,
  signal: AbortSignal
) => Promise<T[]>;

export type SearchStatus = "idle" | "loading" | "results" | "empty" | "error";

export interface DebouncedSearchOptions {
  minLength?: number; // default 3  (backend @Size min)
  maxLength?: number; // default 64 (backend @Size max)
  debounceMs?: number; // default 300
  /**
   * Scope/identity of the search target, as a PRIMITIVE (string|number). On a
   * change of this VALUE the hook: (1) aborts any in-flight request,
   * (2) clears results/error immediately (so an OLD-scope response can never
   * populate the SAME query under a NEW scope), then (3) PRESERVES the current
   * raw query and — if the normalized query is still >= minLength —
   * automatically kicks off a fresh DEBOUNCED search under the new scope; if
   * the query is too short it returns to `idle`. The query input is never
   * cleared. The effect keys on this primitive value, NOT on the options
   * object identity, so re-rendering with a new options object that carries the
   * same scopeKey value is a no-op. PR 1 omits it (single global scope). PR 2
   * passes e.g. `${marketplaceId}:${branchMarketplaceId}`.
   */
  scopeKey?: string | number;
}

export interface DebouncedSearch<T> {
  query: string; // RAW controlled <input> value
  onQueryChange: (raw: string) => void; // wire to <input onChange>
  results: T[]; // ALWAYS belongs to the current normalized query (stale-cleared)
  status: SearchStatus; // drives which row block renders
  loading: boolean; // === (status === 'loading')
  error: boolean; // === (status === 'error')
  reset: () => void; // clear timer + abort + clear state (call on select)
}

export function useDebouncedSearch<T>(
  searchFn: SearchFn<T>,
  options?: DebouncedSearchOptions
): DebouncedSearch<T> {
  const minLength = options?.minLength ?? 3;
  const maxLength = options?.maxLength ?? 64;
  const debounceMs = options?.debounceMs ?? 300;
  const scopeKey = options?.scopeKey;

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const queryRef = useRef("");

  // Always call the latest searchFn / scheduleSearch without making them
  // effect dependencies (PR 2 recreates searchFn each render as a closure).
  const searchFnRef = useRef(searchFn);
  useEffect(() => {
    searchFnRef.current = searchFn;
  });

  const clearTimer = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const runSearch = useCallback((normalizedQuery: string) => {
    const controller = new AbortController();
    abortRef.current = controller;

    searchFnRef
      .current(normalizedQuery, controller.signal)
      .then((rows) => {
        // Stale-response guard: drop late resolves of an aborted request.
        if (controller.signal.aborted) {
          return;
        }
        setResults(rows);
        setStatus(rows.length > 0 ? "results" : "empty");
      })
      .catch(() => {
        // Aborted (cancelled) requests must never flip state.
        if (controller.signal.aborted) {
          return;
        }
        setResults([]);
        setStatus("error");
      });
  }, []);

  const scheduleSearch = useCallback(
    (normalizedQuery: string) => {
      clearTimer();
      // Abort the previous request on every change, synchronously, BEFORE the
      // debounce — so a slow earlier request can never win the race.
      abortInFlight();

      if (
        normalizedQuery.length < minLength ||
        normalizedQuery.length > maxLength
      ) {
        // Out of bounds: no request, no avoidable 400, results cleared.
        setResults([]);
        setStatus("idle");
        return;
      }

      // Clear stale results synchronously: query N's rows are never visible or
      // selectable while query N+1 is loading.
      setResults([]);
      setStatus("loading");
      debounceRef.current = setTimeout(
        () => runSearch(normalizedQuery),
        debounceMs
      );
    },
    [clearTimer, abortInFlight, runSearch, minLength, maxLength, debounceMs]
  );

  const scheduleSearchRef = useRef(scheduleSearch);
  useEffect(() => {
    scheduleSearchRef.current = scheduleSearch;
  });

  const onQueryChange = useCallback((raw: string) => {
    setQuery(raw);
    queryRef.current = raw;
    scheduleSearchRef.current(raw.trim());
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    abortInFlight();
    setQuery("");
    queryRef.current = "";
    setResults([]);
    setStatus("idle");
  }, [clearTimer, abortInFlight]);

  // Scope invalidation (PR 2): abort + clear + preserve query + re-search the
  // preserved query under the new scope. Keyed on the PRIMITIVE scopeKey value,
  // so re-rendering with a fresh options object carrying the same scopeKey is a
  // no-op. Skipped on the initial mount (there is nothing in flight yet).
  const scopeInitialisedRef = useRef(false);
  useEffect(() => {
    if (!scopeInitialisedRef.current) {
      scopeInitialisedRef.current = true;
      return;
    }
    scheduleSearchRef.current(queryRef.current.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Unmount cleanup: cancel the timer and any in-flight request.
  useEffect(() => {
    return () => {
      clearTimer();
      abortInFlight();
    };
  }, [clearTimer, abortInFlight]);

  return {
    query,
    onQueryChange,
    results,
    status,
    loading: status === "loading",
    error: status === "error",
    reset,
  };
}
